import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { GET_CLASS_DATA_PATH } from '../config/apiPaths';
import { type ClassSessionInfo } from '../context/types';
import { type ClassData } from '../types/apiTypes';
import { getUserUuidHeaders } from '../utils/httpUtils';
import { processApiPath } from '../utils/url';

interface ClassDataProps {
    classUuid: string;
    userUuid: string;
    me: any;
    hasAdminPrivileges: boolean;
}

interface ClassDataQuery {
    data: ClassData | null | undefined;
    isFetched: boolean;
    isLoading: boolean;
    error: any | null;
    isError: boolean;
    sessions: ClassSessionInfo[];
    previousSessions: ClassSessionInfo[];
    upcomingSessions: ClassSessionInfo[];
    updateSessions: (classSessions: ClassSessionInfo[]) => void;
    refetch: (options?: any) => Promise<any>;
}
const useClassDataQuery = (props: ClassDataProps): ClassDataQuery => {
    const { classUuid, userUuid, me, hasAdminPrivileges } = props;

    const [sessions, setSessions] = useState<ClassSessionInfo[]>([]);
    const [previousSessions, setPreviousSessions] = useState<ClassSessionInfo[]>([]);
    const [upcomingSessions, setUpcomingSessions] = useState<ClassSessionInfo[]>([]);
    const { data, isFetched, isLoading, error, isError, refetch } = useQuery({
        queryKey: [GET_CLASS_DATA_PATH, classUuid, me?.student_id, hasAdminPrivileges],
        notifyOnChangeProps: ['data', 'error'],
        enabled: !!classUuid && !!userUuid && (Boolean(me) || hasAdminPrivileges),
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const { data: classDataResponse } = await axios.post<ClassData>(
                processApiPath(GET_CLASS_DATA_PATH),
                {
                    class_uuid: classUuid,
                    with_info: true,
                    with_rms_data: true
                },
                { headers: getUserUuidHeaders(userUuid) }
            );

            if (classDataResponse && classDataResponse.classSessions && classDataResponse.classSessionGroups) {
                const classSessionGroupsByUuid = {};

                classDataResponse.classSessionGroups.forEach((classSessionGroup) => {
                    classSessionGroupsByUuid[classSessionGroup.class_session_group_uuid] = classSessionGroup;
                });

                classDataResponse.classSessions.forEach((classSession) => {
                    if (classSession.class_session_group_uuid) {
                        classSession.class_session_group =
                            classSessionGroupsByUuid[classSession.class_session_group_uuid];
                    }
                });
            }
            return classDataResponse;
        }
    });
    const updateSessions = useCallback(
        (classSessions: ClassSessionInfo[]) => {
            setSessions(classSessions);
            const now = new Date().getTime();
            const previous = classSessions.filter((s) => new Date(Number(s.end_time) * 1000).getTime() < now);
            const upcoming = classSessions.filter((s) => new Date(Number(s.end_time) * 1000).getTime() >= now);
            setPreviousSessions(previous);
            setUpcomingSessions(upcoming);
        },
        [setSessions, setPreviousSessions, setUpcomingSessions]
    );
    useEffect(() => {
        if (data) {
            const classSessions = data.classSessions || [];
            updateSessions(classSessions);
        }
    }, [data, updateSessions]);

    return {
        data,
        isFetched,
        isLoading,
        error,
        isError,
        refetch,
        updateSessions,
        sessions,
        previousSessions,
        upcomingSessions
    };
};
export default useClassDataQuery;
